// Data-coverage cross-check: locations that hold stock in the standardized
// inventory feed (`input_inventory_on_hand_pre`) but are absent from the
// atomic master (`input_atomic_master`) are silently dropped from the plan —
// their units appear nowhere downstream. Brooklinen's retail stores are the
// canonical case. Both files live under inputs/{inputId}/ and are small, so
// we read them directly via getRawParquet (signed URL) and diff the
// location sets client-side.
import { useEffect, useState } from "react";
import { usePerspective, useWasm } from "../../contexts/app/provider";
import { GetRawParquetDocument } from "../../services/perspective_api/__generated__/perspective_server-types";
import { parseParquetData, isChunkedParsedData } from "../../utils/parquet";
import { Run } from "../../services/appsync/types";

export type DroppedLocation = {
  location: string;
  units: number;
  skus: number;
};

export type DroppedLocationCheck = {
  /** Locations with stock that the atomic master does not know about. */
  dropped: DroppedLocation[];
  totalUnits: number;
  loading: boolean;
  /** True when either input file could not be read — check not performed. */
  unavailable: boolean;
};

const sumUnitsByLocation = (
  table: import("apache-arrow").Table
): Map<string, { units: number; skus: number }> => {
  const locVec = table.getChild("location");
  const qtyVec = table.getChild("quantity");
  const out = new Map<string, { units: number; skus: number }>();
  if (!locVec) {
    return out;
  }
  for (let i = 0; i < table.numRows; i++) {
    const loc = locVec.get(i);
    if (loc == null) {
      continue;
    }
    const key = String(loc);
    const qty = Number(qtyVec?.get(i) ?? 0);
    const acc = out.get(key) ?? { units: 0, skus: 0 };
    acc.units += Number.isNaN(qty) ? 0 : qty;
    acc.skus += 1;
    out.set(key, acc);
  }
  return out;
};

const uniqueLocations = (
  table: import("apache-arrow").Table
): Set<string> => {
  const locVec = table.getChild("location");
  const out = new Set<string>();
  if (!locVec) {
    return out;
  }
  for (let i = 0; i < table.numRows; i++) {
    const loc = locVec.get(i);
    if (loc != null) {
      out.add(String(loc));
    }
  }
  return out;
};

export const useDroppedLocationCheck = ({
  run,
}: {
  run: Run | null;
}): DroppedLocationCheck => {
  const { perspectiveApiService } = usePerspective();
  const { wasmInitialized } = useWasm();
  const [state, setState] = useState<DroppedLocationCheck>({
    dropped: [],
    totalUnits: 0,
    loading: false,
    unavailable: false,
  });
  const inputId = run?.runSettings.inputId ?? null;

  useEffect(() => {
    if (!inputId || !wasmInitialized) {
      setState({ dropped: [], totalUnits: 0, loading: !!inputId, unavailable: false });
      return;
    }
    let cancelled = false;
    setState({ dropped: [], totalUnits: 0, loading: true, unavailable: false });
    const client = perspectiveApiService.getClient();

    const fetchTable = async (datasetName: string) => {
      const result = await client.query({
        query: GetRawParquetDocument,
        variables: { datasetName, folder: "inputs", setId: inputId, format: "parquet" },
      });
      const url = result.data?.getRawParquet;
      if (!url || typeof url !== "string") {
        return null;
      }
      const parsed = await parseParquetData(url, !url.startsWith("http"));
      return parsed && !isChunkedParsedData(parsed) ? parsed.arrowTable : null;
    };

    Promise.all([
      fetchTable("input_inventory_on_hand_pre"),
      fetchTable("input_atomic_master"),
    ])
      .then(([inventory, master]) => {
        if (cancelled) {
          return;
        }
        if (!inventory || !master) {
          setState({
            dropped: [],
            totalUnits: 0,
            loading: false,
            unavailable: true,
          });
          return;
        }
        const invByLocation = sumUnitsByLocation(inventory);
        const masterLocations = uniqueLocations(master);
        const dropped = [...invByLocation.entries()]
          .filter(([loc, agg]) => !masterLocations.has(loc) && agg.units > 0)
          .map(([location, agg]) => ({
            location,
            units: agg.units,
            skus: agg.skus,
          }))
          .sort((a, b) => b.units - a.units);
        setState({
          dropped,
          totalUnits: dropped.reduce((a, d) => a + d.units, 0),
          loading: false,
          unavailable: false,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setState({
            dropped: [],
            totalUnits: 0,
            loading: false,
            unavailable: true,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [inputId, wasmInitialized, perspectiveApiService]);

  return state;
};
